import { useCallback, useEffect, useRef, useState } from 'react';
import { getEvidencePhotoLinks } from './api';
import { EvidencePhoto, EvidencePhotoLink } from './model';

export interface PhotoLinksState {
  /** photoId → link, for the photos on the current page. */
  links: Map<string, EvidencePhotoLink>;
  loading: boolean;
  failed: boolean;
  /** Fetch the page's links again (e.g. after a link expired). */
  retry: () => void;
}

/**
 * Fetches short-lived links for one page of photos in a single batch request, whenever the page
 * changes. Links are never cached across pages: they expire, and a page shown again gets fresh ones.
 */
export const usePhotoLinks = (warehouseId: string, photos: EvidencePhoto[]): PhotoLinksState => {
  const [links, setLinks] = useState<Map<string, EvidencePhotoLink>>(new Map());
  // Loading from the first render, so a page about to be fetched never flashes "Image not available".
  const [loading, setLoading] = useState(photos.length > 0);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const pageKey = photos.map(photo => photo.photoId).join('|');
  // The site and page whose links are shown, so a retry of the same page keeps them on screen.
  const shownPage = useRef<string>();

  useEffect(() => {
    if (photos.length === 0) return undefined;
    let cancelled = false;
    setLoading(true);
    setFailed(false);
    const requestedPage = `${warehouseId}|${pageKey}`;
    if (shownPage.current !== requestedPage) {
      shownPage.current = requestedPage;
      setLinks(new Map());
    }
    getEvidencePhotoLinks({
      warehouseId,
      photos: photos.map(photo => ({ photoId: photo.photoId, ofd: photo.ofd })),
    })
      .then(response => {
        if (!cancelled) setLinks(new Map(response.links.map(link => [link.photoId, link])));
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // The page's identity is its photo ids; `photos` is a new array every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [warehouseId, pageKey, attempt]);

  const retry = useCallback(() => setAttempt(current => current + 1), []);
  return { links, loading, failed, retry };
};
